import React, { useRef, useState, useEffect, useCallback } from "react";

function CustomCursor({ hidden = false }) {
  const dotRef = useRef(null);
  const outlineRef = useRef(null);
  const mouse = useRef({ x: -100, y: -100 });
  const circle = useRef({ x: -100, y: -100 });
  const [hovering, setHovering] = useState(false);
  const [visible, setVisible] = useState(false);
  const [enabled, setEnabled] = useState(false);

  const checkDesktop = useCallback(() => {
    return (
      window.matchMedia("(pointer: fine)").matches &&
      window.matchMedia("(min-width: 768px)").matches
    );
  }, []);

  useEffect(() => {
    if (!checkDesktop()) return;
    setEnabled(true);

    const onMove = (e) => {
      mouse.current = { x: e.clientX, y: e.clientY };
      if (dotRef.current) {
        dotRef.current.style.transform = `translate3d(${e.clientX}px, ${e.clientY}px, 0)`;
      }
      setVisible(true);
    };

    const onOver = (e) => {
      const hit = e.target.closest(
        'a, button, [role="button"], input, textarea, select, label'
      );
      setHovering(!!hit);
    };

    const onLeave = () => setVisible(false);
    const onEnter = () => setVisible(true);

    document.addEventListener("mousemove", onMove, { passive: true });
    document.addEventListener("mouseover", onOver, { passive: true });
    document.addEventListener("mouseleave", onLeave);
    document.addEventListener("mouseenter", onEnter);

    let raf;
    const lerp = () => {
      circle.current.x += (mouse.current.x - circle.current.x) * 0.12;
      circle.current.y += (mouse.current.y - circle.current.y) * 0.12;
      if (outlineRef.current) {
        outlineRef.current.style.transform = `translate3d(${circle.current.x}px, ${circle.current.y}px, 0)`;
      }
      raf = requestAnimationFrame(lerp);
    };
    lerp();

    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseover", onOver);
      document.removeEventListener("mouseleave", onLeave);
      document.removeEventListener("mouseenter", onEnter);
      cancelAnimationFrame(raf);
    };
  }, [checkDesktop]);

  if (!enabled) return null;

  // In CLI mode the iframe captures all pointer events — hide both cursor
  // elements so the frozen dot doesn't ghost on screen.
  const cursorVisible = visible && !hidden;

  return (
    <>
      <div
        ref={dotRef}
        className="fixed top-0 left-0 z-[9999] pointer-events-none mix-blend-difference"
        style={{
          willChange: "transform",
          opacity: cursorVisible ? 1 : 0,
          transition: "opacity 0.15s",
        }}
      >
        <div
          className={`rounded-full bg-white transition-all duration-200 ${
            hovering
              ? "w-3 h-3 -ml-1.5 -mt-1.5"
              : "w-1.5 h-1.5 -ml-[3px] -mt-[3px]"
          }`}
        />
      </div>
      <div
        ref={outlineRef}
        className="fixed top-0 left-0 z-[9998] pointer-events-none mix-blend-difference"
        style={{
          willChange: "transform",
          opacity: cursorVisible ? 1 : 0,
          transition: "opacity 0.15s",
        }}
      >
        <div
          className={`rounded-full border transition-all duration-300 ${
            hovering
              ? "w-12 h-12 -ml-6 -mt-6 border-white/40"
              : "w-8 h-8 -ml-4 -mt-4 border-white/20"
          }`}
        />
      </div>
    </>
  );
}

export default React.memo(CustomCursor);
